import Link from "next/link";
import { LeerZustand } from "../leerzustand";
import { breiteKlasse, cx, prozent } from "./breite";

export interface Anteil {
  label: string;
  wert: number;
}

const ZAHL = new Intl.NumberFormat("de-DE");

const SCHRAFFUR_BALKEN =
  "overflow-hidden bg-[linear-gradient(135deg,var(--color-chart-3)_25%,transparent_25%,transparent_50%,var(--color-chart-3)_50%,var(--color-chart-3)_75%,transparent_75%,transparent)] bg-size-[7px_7px] rounded-sm";
const ZEILEN_BALKEN = "absolute inset-y-0 inset-s-0 bg-primary-100 h-full rounded-sm dark:bg-primary-500/20";

/**
 * Zweiteilung (oben, voll vs. schraffiert) über einer Aufschlüsselungsliste mit
 * Hintergrundbalken (Breite relativ zum größten Eintrag, wie im Original 100 % = erster Eintrag).
 * Das Original steht ohne eigenen Rahmen in einer Seitenleiste; hier sitzt es in der
 * Kartenhülle von goal-progress-bar-card, damit es nicht frei auf dem Seitenhintergrund liegt.
 */
export function AufschluesselungKarte({
  id,
  titel,
  untertitel,
  link,
  teilung,
  zeilen,
  leer,
}: {
  id: string;
  titel: string;
  untertitel: string;
  link?: { href: string; text: string };
  /** Zweiteilung oben (links voll, rechts schraffiert). */
  teilung: { links: Anteil; rechts: Anteil };
  zeilen: Anteil[];
  leer: { titel: string; text: string };
}) {
  const titelId = `${id}-titel`;
  const summe = teilung.links.wert + teilung.rechts.wert;
  const max = Math.max(0, ...zeilen.map((z) => z.wert));
  const pLinks = prozent(teilung.links.wert, summe);
  const pRechts = summe > 0 ? 100 - pLinks : 0;
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/goal-progress-bar-card */}
      <section aria-labelledby={titelId} className="flex flex-col bg-card border border-card-line shadow-xs rounded-xl overflow-hidden">
        {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/segmented-breakdown-progress-card */}
        <div className="p-4 border-b border-line-2">
          <div className="flex flex-col">
            {/* Header */}
            <div className="relative">
              <div className="pe-20">
                <h2 id={titelId} className="font-medium text-sm text-foreground">
                  {titel}
                </h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground-1">{untertitel}</p>
                </div>
              </div>

              {link && (
                <div className="absolute top-1/2 inset-e-0 -translate-y-1/2">
                  <Link
                    href={link.href}
                    aria-label={link.text}
                    className="group size-7 lg:size-auto lg:py-1.5 lg:px-2 flex items-center justify-center border border-layer-line text-layer-foreground text-xs rounded-full py-1 hover:bg-primary-50 hover:border-primary-100 hover:text-primary-700 focus:outline-none focus:bg-primary-50 focus:border-primary-100 focus:text-primary-700 dark:hover:bg-primary-500/20 dark:hover:border-primary-500/20 dark:hover:text-primary-400 dark:focus:bg-primary-500/20 dark:focus:border-primary-500/20 dark:focus:text-primary-400"
                  >
                    <span className="lg:block hidden max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:me-1 group-hover:max-w-25 group-hover:opacity-100 group-focus:me-1 group-focus:max-w-25 group-focus:opacity-100">
                      {link.text}
                    </span>
                    <svg className="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M15 3h6v6" />
                      <path d="m21 3-7 7" />
                      <path d="m3 21 7-7" />
                      <path d="M9 21H3v-6" />
                    </svg>
                  </Link>
                </div>
              )}
            </div>
            {/* End Header */}

            {summe === 0 ? (
              <LeerZustand titel={leer.titel} text={leer.text} />
            ) : (
              <>
                {/* Legend Indicator */}
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <div className="flex flex-col">
                    <p className="text-xl text-foreground">{ZAHL.format(teilung.links.wert)}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="shrink-0 w-3 h-1.5 inline-block bg-chart-primary rounded-full" />
                      <div className="grow">
                        <span className="block text-sm text-muted-foreground-1">{teilung.links.label}</span>
                      </div>
                    </div>
                  </div>
                  {/* End Col */}

                  <div className="flex flex-col items-end">
                    <p className="text-xl text-foreground">{ZAHL.format(teilung.rechts.wert)}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="shrink-0 w-3 h-1.5 inline-block bg-[linear-gradient(135deg,var(--color-chart-3)_25%,transparent_25%,transparent_50%,var(--color-chart-3)_50%,var(--color-chart-3)_75%,transparent_75%,transparent)] bg-size-[4px_4px] rounded-full" />
                      <div className="grow">
                        <span className="block text-sm text-muted-foreground-1">{teilung.rechts.label}</span>
                      </div>
                    </div>
                  </div>
                  {/* End Col */}
                </div>
                {/* End Legend Indicator */}

                <div className="mt-3 flex gap-x-1.5 w-full h-3.5 rounded-full">
                  {pLinks > 0 && (
                    <div
                      className={cx("bg-primary rounded-sm", breiteKlasse(pLinks))}
                      role="progressbar"
                      aria-label={teilung.links.label}
                      aria-valuenow={pLinks}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  )}
                  {pRechts > 0 && (
                    <div
                      className={cx(SCHRAFFUR_BALKEN, breiteKlasse(pRechts))}
                      role="progressbar"
                      aria-label={teilung.rechts.label}
                      aria-valuenow={pRechts}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {zeilen.length > 0 && (
          <div className="p-4 border-b border-line-2">
            {/* List Group */}
            <ul className="flex flex-col gap-y-2">
              {zeilen.map((z) => (
                <li key={z.label} className="flex justify-between items-center gap-x-2">
                  <div className="relative size-full truncate">
                    <span className="relative z-1 inline-flex py-1 px-2 w-full text-sm truncate">
                      <span className="text-foreground">{z.label}</span>
                      <span className="ms-auto text-muted-foreground-1">{ZAHL.format(z.wert)}</span>
                    </span>
                    <div className={cx(ZEILEN_BALKEN, breiteKlasse(prozent(z.wert, max)))} />
                  </div>
                </li>
              ))}
            </ul>
            {/* End List Group */}
          </div>
        )}
      </section>
    </>
  );
}
